import {useCallback, useEffect, useMemo, useRef} from 'react';
import equal from 'fast-deep-equal';
import {useDispatch, useSelector} from 'react-redux';
import {MiddlewareAPI, Dispatch, AnyAction} from 'redux';
import {createByIdApiFactory} from '../api';
import {QueryParameters} from '../api/types';
import {EntityAction, createEntityReduxFactory, EntityState} from '../redux';
import {createSubscribeEntityInScope} from '../datasync';
import {createEntityDataSyncService} from '../datasync/createEntityDataSyncService';
import {EntityOperations} from '../datasync/types';
import {INewMockDataInstance} from '../api/fetchers/createMockDataApi/types';
import {query} from '@di-nxt/query';
import {ApiAuthTokens, GetConfigFunc} from '../types';
import {
  ApiOperationError,
  ApiOperationStatus,
  ApiOperationsWithAuthToken,
  ErrorHandler,
  LoadStatus,
  PartialData,
} from './types';
import {getRequestKey} from './functions';

export const createResourceContainer = <
  TData extends PartialData<TIdKey, any>,
  // eslint-disable-next-line @typescript-eslint/ban-types
  TParameters extends QueryParameters = {},
  TPayload = Record<string, unknown>,
  TEntityName extends string = any,
  TIdKey extends keyof TData = keyof TData
>(config: {
  scopeKey?: keyof TParameters;
  entityName: TEntityName;
  idKey: TIdKey;
  endPoint: string;
  getConfig: GetConfigFunc;
  mockData: TData[] | ((parameters: TParameters) => TData[]);
  mockHook?: (storeApi: MiddlewareAPI, action: any) => void;
  mockIdFactory: () => TData[TIdKey];
  mockFactory?: INewMockDataInstance<TData, TIdKey, TPayload>;
}) => {
  const {
    scopeKey,
    idKey,
    entityName,
    endPoint,
    mockData,
    mockIdFactory,
    mockFactory,
    mockHook,
    getConfig,
  } = config;

  const mode = getConfig().Api.Mode;
  const baseUrl = getConfig().Api.BaseUrl;
  const mockFailureRate = getConfig().Api.Mock.FailureRate;

  // Load statuses
  let loadingStatuses: {
    [key: string]: {
      status: LoadStatus;
      activePromise?: Promise<void>;
      error?: Error;
    };
  } = {};

  const middlewareHook = (
    api: MiddlewareAPI<
      Dispatch<AnyAction>,
      {[key in TEntityName]: EntityState<TData, TIdKey>}
    >,
    action: EntityAction<TData, TIdKey, TEntityName>,
  ) => {
    if (mode === 'mock' && mockHook) {
      mockHook(api, action);
    }

    if (action.type === 'RESET' || action.type === `RESET_${entityName}`) {
      console.log('Reset loadingStatuses:', entityName);
      loadingStatuses = {};
    }
  };

  // Redux
  const redux = createEntityReduxFactory<TData, TEntityName, TIdKey>(
    entityName,
    idKey,
    middlewareHook,
  );

  // Api
  const api = createByIdApiFactory<TData, TIdKey, TParameters, TPayload>({
    idKey,
    baseUrl,
    endPoint,
    mockData,
    mockIdFactory,
    mockFactory: mockFactory,
    mode,
    mockFailureRate,
  });

  // Response
  const response = createEntityDataSyncService<TData, TEntityName, TIdKey>(
    getConfig,
  );

  // Update Redux from Pusher
  const useEntityOperations = (): EntityOperations<TData, TIdKey> => {
    const dispatch = useDispatch();
    // Used for initialization
    return useMemo(
      () => ({
        remove: (id: TData[TIdKey][]) => dispatch(redux.actions.remove(id)),
        update: (id: TData[TIdKey], data: Partial<Omit<TData, TIdKey>>) => {
          dispatch(redux.actions.update(id, data));
        },
        add: (data: TData[]) => dispatch(redux.actions.add(data)),
        replace: (data: TData[]) => dispatch(redux.actions.replace(data)),
      }),
      [dispatch],
    );
  };

  // Create subscription
  const {subscribe} = createSubscribeEntityInScope<TData, TEntityName, TIdKey>(
    entityName,
    idKey,
    response,
  );

  // create useSelector hook for lists filtered on parameters
  const useResources = (
    filter?: Partial<TData> | ((item: TData) => boolean),
  ): TData[] => {
    const data = useSelector(redux.selectors.all);
    // TODO: Bjørn Egil - look at this one
    //const version = useSelector(redux.selectors.version);
    const prevDataRef = useRef<TData[]>([]);
    //const prevVersionRef = useRef(-1);

    //    if (version !== prevVersionRef.current) {
    const nextData =
      filter === undefined ? data : query(data).where(filter).toArray();
    if (!equal(nextData, prevDataRef.current)) {
      prevDataRef.current = nextData;
    }
    //  prevVersionRef.current = version;
    //  }

    return prevDataRef.current;
  };

  // create useSelector hook for single instances
  const useResource = (id: TData[TIdKey]): TData | undefined => {
    const selector = useMemo(() => redux.selectors.single(id), [id]);
    return useSelector(selector);
  };

  // Connect Redux and API
  const useApiOperations = () => {
    const dispatch = useDispatch();

    // Coordinated loader
    const coordinatedLoader = useCallback(
      (
        parameters: Record<string, unknown> | undefined,
        loadRequest: () => Promise<TData[]>,
        dispatchInner: Dispatch,
      ) => {
        const key = getRequestKey(parameters, entityName);
        const status = loadingStatuses[key]?.status ?? 'NotLoaded';
        if (status === 'NotLoaded') {
          console.log(
            'coordinatedLoader: Not loaded => load',
            entityName,
            'with key',
            key,
          );

          const loadingPromise = loadRequest()
            .then(data => {
              dispatchInner(redux.actions.add(data));
              loadingStatuses[key] = {status: 'Loaded'};
              console.log('Got', data.length, entityName);
            })
            .catch(err => {
              loadingStatuses[key] = {status: 'Failed', error: err};
              throw new ApiOperationError(entityName, 'get', err, parameters);
            });

          loadingStatuses[key] = {
            status: 'Loading',
            activePromise: loadingPromise,
          };
        } else {
          console.log(
            'coordinatedLoader:',
            entityName,
            status,
            'with key',
            key,
          );
        }

        return loadingStatuses[key].activePromise ?? Promise.resolve();
      },
      [],
    );

    // Get operations
    const getItem = useCallback(
      (id: TData[TIdKey], authTokens?: ApiAuthTokens) =>
        coordinatedLoader(
          {[idKey]: id},
          () => api.get(id, authTokens?.accessToken),
          dispatch,
        ),
      [coordinatedLoader, dispatch],
    );

    const getList = useCallback(
      (parameters?: TParameters, authTokens?: ApiAuthTokens) =>
        coordinatedLoader(
          parameters,
          () => api.get(parameters, authTokens?.accessToken),
          dispatch,
        ),
      [coordinatedLoader, dispatch],
    );

    // Update
    const update = useCallback(
      (
        id: TData[TIdKey],
        data: Omit<TData, TIdKey>,
        authTokens?: ApiAuthTokens,
      ) =>
        api
          .update(id, data, authTokens?.accessToken)
          .then(() => {
            dispatch(redux.actions.update(id, data));
            return ApiOperationStatus.OK;
          })
          .catch(err => {
            throw new ApiOperationError(entityName, 'update', err, {id, data});
          }),
      [dispatch],
    );

    // Remove
    const remove = useCallback(
      (id: TData[TIdKey], authTokens?: ApiAuthTokens) =>
        api
          .remove(id, authTokens?.accessToken)
          .then(() => {
            dispatch(redux.actions.remove([id]));
            return ApiOperationStatus.OK;
          })
          .catch(err => {
            throw new ApiOperationError(entityName, 'remove', err, {id});
          }),
      [dispatch],
    );

    // Create
    const create = useCallback(
      (payload: TPayload, authTokens?: ApiAuthTokens) =>
        api
          .create(payload, authTokens?.accessToken)
          .then(data => {
            dispatch(redux.actions.add([data]));
            return {status: ApiOperationStatus.OK, data};
          })
          .catch(err => {
            throw new ApiOperationError(entityName, 'create', err, {payload});
          }),
      [dispatch],
    );

    // Reset
    const reset = useCallback(() => {
      dispatch(redux.actions.reset());
    }, [dispatch]);

    const apiOperations: ApiOperationsWithAuthToken<
      TData,
      TParameters,
      TPayload,
      TIdKey
    > = {getItem, getList, update, create, remove};

    return {...apiOperations, reset};
  };

  // Create loader component
  type Props = TParameters & {
    authTokens: ApiAuthTokens | undefined;
    onError?: ErrorHandler;
  };
  const LoaderComponent = (props: Props) => {
    const {authTokens, onError, ...rest} = props;
    const parameters: TParameters = (rest as unknown) as TParameters;

    const {getList} = useApiOperations();
    const entityOperations = useEntityOperations();
    const errorHandler = useCallback(
      (error: Error) => {
        const unhandledError = onError ? onError(error) : error;

        if (unhandledError) {
          console.log(
            'LoaderComponent:',
            entityName,
            ' Unhandled error:',
            unhandledError.message,
          );
          throw unhandledError;
        }
      },
      [onError],
    );

    // Handle reloading when authentication changes
    const prevAuthTokensRef = useRef<ApiAuthTokens>();
    const prevParamsRef = useRef<TParameters>();
    useEffect(() => {
      if (
        authTokens &&
        (prevAuthTokensRef.current?.accessToken !== authTokens.accessToken ||
          !equal(parameters, prevParamsRef.current))
      ) {
        prevAuthTokensRef.current = authTokens;
        prevParamsRef.current = parameters;
        getList(parameters, authTokens).catch(errorHandler);
        subscribe(
          authTokens.accessToken,
          // Since parameters can contain string, number OR boolean we
          // need to cast scope key to string | number  - since that's what we're expecting
          // The reason is that we're using the scopeKey as an indexer and that
          // the indexer needs to be a valid key (string | number). TODO: Fix this
          scopeKey
            ? ((parameters[scopeKey] as any) as string | number)
            : undefined,
          entityOperations,
        );
      }
    }, [authTokens, entityOperations, errorHandler, getList, parameters]);

    return null;
  };

  return {
    redux,
    useOperations: useApiOperations,
    useResource,
    useResources,
    component: LoaderComponent,
  };
};
